import { z } from "zod";

export const UTM_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export const utmAttributionSchema = z.object({
  source: z.string().trim().max(200).optional(),
  medium: z.string().trim().max(200).optional(),
  campaign: z.string().trim().max(200).optional(),
  content: z.string().trim().max(200).optional(),
  term: z.string().trim().max(200).optional(),
});

export type UtmAttribution = z.infer<typeof utmAttributionSchema>;

const STORAGE_KEY = "idealitika:utm:v1";

function hasAnyUtm(utm: UtmAttribution): boolean {
  return Boolean(
    utm.source || utm.medium || utm.campaign || utm.content || utm.term,
  );
}

/** Разобрать utm_* из query string. */
export function parseUtmFromSearch(search: string): UtmAttribution {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const next: UtmAttribution = {};
  const source = params.get("utm_source")?.trim();
  const medium = params.get("utm_medium")?.trim();
  const campaign = params.get("utm_campaign")?.trim();
  const content = params.get("utm_content")?.trim();
  const term = params.get("utm_term")?.trim();
  if (source) next.source = source.slice(0, 200);
  if (medium) next.medium = medium.slice(0, 200);
  if (campaign) next.campaign = campaign.slice(0, 200);
  if (content) next.content = content.slice(0, 200);
  if (term) next.term = term.slice(0, 200);
  return next;
}

/** Сохранить utm из URL (last-touch в рамках сессии). */
export function captureUtmFromSearch(search: string): void {
  if (typeof window === "undefined") return;
  const parsed = parseUtmFromSearch(search);
  if (!hasAnyUtm(parsed)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    /* ignore quota / private mode */
  }
}

/** UTM для отправки с формой (после captureUtmFromSearch). */
export function readStoredUtm(): UtmAttribution | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = utmAttributionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || !hasAnyUtm(parsed.data)) return undefined;
    return parsed.data;
  } catch {
    return undefined;
  }
}

export function formatUtmForComments(utm: UtmAttribution): string {
  const lines = [
    utm.source ? `utm_source: ${utm.source}` : null,
    utm.medium ? `utm_medium: ${utm.medium}` : null,
    utm.campaign ? `utm_campaign: ${utm.campaign}` : null,
    utm.content ? `utm_content: ${utm.content}` : null,
    utm.term ? `utm_term: ${utm.term}` : null,
  ].filter(Boolean);
  return lines.length ? ["UTM:", ...lines].join("\n") : "";
}
